import React from 'react';
import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { SearchAdvancedFilters, CAPITULOS_MAX_PADRAO } from './SearchAdvancedFilters';
import type { SelectOption } from '@/components/ui/Select';

interface FilterOption {
    id: number;
    nome: string;
}

interface SearchFiltersModalProps {
    open: boolean;
    onClose: () => void;
    statuses: FilterOption[];
    selectedStatuses: number[];
    onStatusChange: (ids: number[]) => void;
    tags: FilterOption[];
    selectedTags: number[];
    onTagsChange: (ids: number[]) => void;
    loadingTags: boolean;
    ordenarPor: string;
    ordenarOptions: SelectOption[];
    onOrdenarChange: (value: string) => void;
    minCapitulos: number;
    maxCapitulos: number;
    onCapitulosChange: (min: number, max: number) => void;
    activeFiltersCount: number;
    onClearAll: () => void;
}

export const SearchFiltersModal: React.FC<SearchFiltersModalProps> = ({
    open,
    onClose,
    activeFiltersCount,
    onClearAll,
    ...filterProps
}) => {
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                        className="fixed inset-x-0 bottom-0 z-[201] flex max-h-[calc(100dvh-env(safe-area-inset-top)-10px)] flex-col justify-end md:inset-0 md:max-h-none md:flex-row md:items-center md:justify-center md:p-4"
                    >
                        <div
                            className="flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-neutral-950 shadow-2xl md:max-h-[85vh] md:max-w-2xl md:rounded-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-5 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] md:py-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="text-sm font-black uppercase tracking-widest text-white">Filtros</span>
                                    {activeFiltersCount > 0 && (
                                        <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-black text-white">
                                            {activeFiltersCount}
                                        </span>
                                    )}
                                </div>
                                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                                    {activeFiltersCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={onClearAll}
                                            className="text-xs font-semibold text-orange-400 transition-colors hover:text-orange-300"
                                        >
                                            Limpar tudo
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        aria-label="Fechar filtros"
                                        className="flex h-9 w-9 items-center justify-center rounded-lg text-white/40 transition-colors hover:bg-white/[0.06] hover:text-white"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-5">
                                <SearchAdvancedFilters
                                    statuses={filterProps.statuses}
                                    selectedStatuses={filterProps.selectedStatuses}
                                    onStatusChange={filterProps.onStatusChange}
                                    tags={filterProps.tags}
                                    selectedTags={filterProps.selectedTags}
                                    onTagsChange={filterProps.onTagsChange}
                                    loadingTags={filterProps.loadingTags}
                                    ordenarPor={filterProps.ordenarPor}
                                    ordenarOptions={filterProps.ordenarOptions}
                                    onOrdenarChange={filterProps.onOrdenarChange}
                                    minCapitulos={filterProps.minCapitulos}
                                    maxCapitulos={filterProps.maxCapitulos}
                                    onCapitulosChange={filterProps.onCapitulosChange}
                                />
                            </div>

                            <div className="shrink-0 border-t border-white/10 px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 md:pb-safe md:py-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="w-full rounded-xl bg-orange-600 py-3 text-sm font-black uppercase tracking-widest text-white transition-colors hover:bg-orange-500"
                                >
                                    Ver resultados
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export { CAPITULOS_MAX_PADRAO };
